// Маленький безопасный markdown: заголовки, жирный/курсив, код, списки, цитаты, таблицы, ссылки.
// Строит React-элементы (никакого innerHTML), поэтому любой HTML в тексте выводится как текст.
import { useMemo, type ReactNode } from 'react'
import { Icon } from '../components/Icon.tsx'
import { TaskLink, navigate } from '../router.tsx'

const LIST_RE = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/
const FENCE_RE = /^\s*(```+|~~~+)\s*([\w+#.-]*)\s*$/
const HEADING_RE = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/
const HR_RE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/

function indentOf(s: string): number {
  const m = /^\s*/.exec(s)
  return m ? m[0].replace(/\t/g, '    ').length : 0
}

function isBlockStart(line: string): boolean {
  return FENCE_RE.test(line) || HEADING_RE.test(line) || HR_RE.test(line) || LIST_RE.test(line) || /^\s*>/.test(line)
}

/** Ячейки строки таблицы: «|» внутри `кода` и экранированный «\|» разделителями не считаются. */
function splitRow(line: string): string[] {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1)
  const cells: string[] = []
  let cur = ''
  let tick = 0
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!
    if (ch === '\\' && s[i + 1] === '|') {
      cur += '|'
      i++
      continue
    }
    if (ch === '`') {
      let n = 1
      while (s[i + n] === '`') n++
      if (tick === 0) tick = n
      else if (tick === n) tick = 0
      cur += s.slice(i, i + n)
      i += n - 1
      continue
    }
    if (ch === '|' && tick === 0) {
      cells.push(cur.trim())
      cur = ''
      continue
    }
    cur += ch
  }
  cells.push(cur.trim())
  return cells
}

/** base — уровень HTML-заголовка для «#»: 3 (по умолчанию) делает # → h3, ## → h4…; 1 — # → h1, ### → h3. */
export function parseBlocks(src: string, keyPrefix = 'b', base = 3): ReactNode[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n')
  const out: ReactNode[] = []
  let i = 0
  let k = 0
  const key = () => `${keyPrefix}${k++}`

  while (i < lines.length) {
    const line = lines[i]!

    if (!line.trim()) {
      i++
      continue
    }

    const fence = FENCE_RE.exec(line)
    if (fence) {
      const marker = fence[1]!
      const buf: string[] = []
      i++
      while (i < lines.length && !lines[i]!.trim().startsWith(marker)) buf.push(lines[i++]!)
      i++
      out.push(
        <pre key={key()}>
          <code>{buf.join('\n')}</code>
        </pre>,
      )
      continue
    }

    const h = HEADING_RE.exec(line)
    if (h) {
      const level = Math.min(6, Math.max(1, base + h[1]!.length - 1))
      const content = inline(h[2]!, key())
      const kk = key()
      if (level === 1) out.push(<h1 key={kk}>{content}</h1>)
      else if (level === 2) out.push(<h2 key={kk}>{content}</h2>)
      else if (level === 3) out.push(<h3 key={kk}>{content}</h3>)
      else if (level === 4) out.push(<h4 key={kk}>{content}</h4>)
      else if (level === 5) out.push(<h5 key={kk}>{content}</h5>)
      else out.push(<h6 key={kk}>{content}</h6>)
      i++
      continue
    }

    if (HR_RE.test(line)) {
      out.push(<hr key={key()} />)
      i++
      continue
    }

    if (/^\s*>/.test(line)) {
      const buf: string[] = []
      while (i < lines.length && /^\s*>/.test(lines[i]!)) buf.push(lines[i++]!.replace(/^\s*>\s?/, ''))
      const kk = key()
      out.push(<blockquote key={kk}>{parseBlocks(buf.join('\n'), `${kk}-`, base)}</blockquote>)
      continue
    }

    const lm = LIST_RE.exec(line)
    if (lm) {
      const base = indentOf(line)
      const ordered = /\d/.test(lm[2]!)
      const start = ordered ? parseInt(lm[2]!, 10) : 1
      const items: string[][] = []
      while (i < lines.length) {
        const l = lines[i]!
        const m = LIST_RE.exec(l)
        if (m && indentOf(l) === base && /\d/.test(m[2]!) === ordered) {
          items.push([m[3]!])
          i++
          continue
        }
        if (!l.trim()) {
          const next = lines[i + 1]
          if (next !== undefined && next.trim() && indentOf(next) > base) {
            items[items.length - 1]!.push('')
            i++
            continue
          }
          break
        }
        if (indentOf(l) > base && items.length) {
          items[items.length - 1]!.push(l.slice(Math.min(indentOf(l), base + 2)))
          i++
          continue
        }
        if (m && indentOf(l) < base) break
        if (!m && !isBlockStart(l) && items.length) {
          // ленивое продолжение пункта
          items[items.length - 1]!.push(l.trim())
          i++
          continue
        }
        break
      }
      const kk = key()
      const lis = items.map((it, idx) => {
        const first = it[0] ?? ''
        const task = /^\[([ xX])\]\s+(.*)$/.exec(first)
        const ik = `${kk}-${idx}`
        const rest = it.slice(1)
        const head = task ? (
          <>
            <span className="mr-1.5 inline-flex translate-y-[2px] text-muted" role="img" aria-label={task[1] === ' ' ? 'не сделано' : 'сделано'}>
              <Icon name={task[1] === ' ' ? 'square' : 'squareCheck'} size={14} />
            </span>
            {inline(task[2]!, `${ik}h`)}
          </>
        ) : (
          inline(first, `${ik}h`)
        )
        return (
          <li key={ik} className={task ? 'task' : undefined}>
            {head}
            {rest.some((r) => r.trim()) ? parseBlocks(rest.join('\n'), `${ik}-`, base) : null}
          </li>
        )
      })
      out.push(
        ordered ? (
          <ol key={kk} start={start !== 1 ? start : undefined}>
            {lis}
          </ol>
        ) : (
          <ul key={kk}>{lis}</ul>
        ),
      )
      continue
    }

    // таблица
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]!)) {
      const head = splitRow(line)
      i += 2
      const rows: string[][] = []
      while (i < lines.length && lines[i]!.includes('|') && lines[i]!.trim()) rows.push(splitRow(lines[i++]!))
      const kk = key()
      out.push(
        // таблица шире экрана прокручивается вбок внутри своего блока (.md-table в theme.css)
        <div key={kk} className="md-table scroll-thin overflow-x-auto">
          <table>
            <thead>
              <tr>
                {head.map((c, ci) => (
                  <th key={ci}>{inline(c, `${kk}h${ci}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {head.map((_, ci) => (
                    <td key={ci}>{inline(r[ci] ?? '', `${kk}r${ri}c${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // абзац: подряд идущие строки, переносы сохраняются
    const buf: string[] = []
    while (i < lines.length && lines[i]!.trim() && (buf.length === 0 || !isBlockStart(lines[i]!))) {
      if (buf.length && lines[i]!.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]!)) break
      buf.push(lines[i++]!)
    }
    const kk = key()
    out.push(
      <p key={kk}>
        {buf.map((l, li) => (
          <span key={li}>
            {li > 0 ? <br /> : null}
            {inline(l.trim(), `${kk}-${li}`)}
          </span>
        ))}
      </p>,
    )
  }
  return out
}

// группы: 1 `-ряд, 2 код | 3 **жирный** | 4 __жирный__ | 5 ~~зачёркнутый~~ | 6 текст ссылки, 7 адрес |
// 8 голая ссылка | 9 номер задачи OMA-N | 10 *курсив* | 11 _курсив_
const INLINE_RE =
  /(`+)([\s\S]*?[^`])\1(?!`)|\*\*([\s\S]+?)\*\*|(?<![\p{L}\p{N}_])__([^_\s](?:[\s\S]*?[^_\s])?)__(?![\p{L}\p{N}_])|~~([\s\S]+?)~~|\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"»])|\bOMA-(\d+)\b|\*([^*\s](?:[^*]*[^*\s])?)\*|(?<![\p{L}\p{N}_])_([^_\s](?:[^_]*[^_\s])?)_(?![\p{L}\p{N}_])/gu

export function safeHref(url: string): string | null {
  const u = url.trim()
  if (/^(https?:|mailto:)/i.test(u)) return u
  if (u.startsWith('/') && !u.startsWith('//')) return u
  return null
}

function renderLink(href: string, children: ReactNode, k: string): ReactNode {
  const internal = href.startsWith('/')
  if (internal) {
    return (
      <a
        key={k}
        href={href}
        onClick={(e) => {
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return
          e.preventDefault()
          navigate(href)
        }}
      >
        {children}
      </a>
    )
  }
  return (
    <a key={k} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}

export function inline(text: string, keyPrefix = 'i'): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let n = 0
  const re = new RegExp(INLINE_RE.source, INLINE_RE.flags)
  let m: RegExpExecArray | null
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const k = `${keyPrefix}.${n++}`
    if (m[2] !== undefined) {
      out.push(<code key={k}>{m[2].replace(/^ (.*) $/, '$1')}</code>)
    } else if (m[3] !== undefined || m[4] !== undefined) {
      out.push(<strong key={k}>{inline(m[3] ?? m[4] ?? '', k)}</strong>)
    } else if (m[5] !== undefined) {
      out.push(<del key={k}>{inline(m[5], k)}</del>)
    } else if (m[6] !== undefined && m[7] !== undefined) {
      const href = safeHref(m[7])
      out.push(href ? renderLink(href, inline(m[6], k), k) : m[0])
    } else if (m[8] !== undefined) {
      out.push(renderLink(m[8], m[8], k))
    } else if (m[9] !== undefined) {
      out.push(
        <TaskLink key={k} id={Number(m[9])}>
          OMA-{m[9]}
        </TaskLink>,
      )
    } else if (m[10] !== undefined || m[11] !== undefined) {
      out.push(<em key={k}>{inline(m[10] ?? m[11] ?? '', k)}</em>)
    } else {
      out.push(m[0])
    }
    last = m.index + m[0].length
    if (m[0].length === 0) re.lastIndex++
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

export function Markdown({
  text,
  className,
  headingBase = 3,
}: {
  text: string | null | undefined
  className?: string
  /** Уровень заголовка для «#» (см. parseBlocks). В справочниках — 1, чтобы «###» стал h3. */
  headingBase?: number
}) {
  const nodes = useMemo(() => parseBlocks(text ?? '', 'b', headingBase), [text, headingBase])
  if (!text || !text.trim()) return null
  return <div className={`md ${className ?? ''}`}>{nodes}</div>
}

/** Фрагмент поиска: совпадения сервер оборачивает в ⟪…⟫ (однозначные маркеры — «ёлочки» в русском тексте
 *  встречаются сами по себе), разметка markdown убирается. */
export function Snippet({ text, className }: { text: string; terms?: string[]; className?: string }) {
  const clean = text
    .replace(/\*\*|__|`/g, '')
    .replace(/^#+\s*/gm, '')
    .replace(/\s*\|\s*/g, ' · ')
    .replace(/\s+/g, ' ')
    .trim()
  const parts = clean.split(/(⟪[^⟪⟫]*⟫)/g)
  return (
    <span className={className}>
      {parts.map((p, i) =>
        p.startsWith('⟪') && p.endsWith('⟫') ? (
          <mark key={i} className="hl">
            {p.slice(1, -1)}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </span>
  )
}
